/**
 * `/konto` — the trainer's own account: profile, brand colour, subscription and this browser's
 * preferences.
 *
 * A wiring declaration. Which parameters this screen understands is the SLICE's decision, so the
 * validator comes from it — `?tab=kolor-marki`, `?tab=subskrypcja`, `?tab=aplikacja`, and no
 * parameter means Profil.
 *
 * ── An unrecognised `?tab=` is DROPPED rather than defaulted into the URL ────────────────────
 * The rule and its reason are `_shell.klienci.$clientId.tsx`'s: writing a default back would rewrite
 * a link the trainer pasted, and the screen falls back on its own anyway. The validator drops the
 * DEFAULT too, so `/konto` and `/konto?tab=profil` do not become two addresses for one screen.
 *
 * ── The search is NEVER spread from the current one ──────────────────────────────────────────
 * `{ ...current, tab }` is what that file warns about at length: TanStack's `applySearchMiddleware`
 * spreads the validator's output OVER the updater's result rather than replacing it, so an unknown
 * key a link arrived with survives every tab press and is re-serialised into the URL forever.
 * `accountTabSearch` builds the whole object from a validated value.
 */

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';

import { AccountScreen, accountTabOf, accountTabSearch, validateAccountSearch } from '~/pages/account';

export const Route = createFileRoute('/_shell/konto')({
  validateSearch: validateAccountSearch,
  component: AccountRoute,
});

function AccountRoute() {
  const search = useSearch({ from: '/_shell/konto' });
  const navigate = useNavigate();

  return (
    <AccountScreen
      // `accountTabOf` runs AGAIN here, not only in `validateSearch`: the router merges the raw
      // search UNDER the validator's output, so a key the validator dropped still arrives —
      // `?tab=garbage` reaches this line as the literal string.
      tab={accountTabOf(search)}
      onSelectTab={(tab) => {
        // PUSHES rather than replaces: moving between tabs is navigation the trainer took, and Back
        // returning to the tab they came from is what a browser is for.
        void navigate({ to: '.', search: () => accountTabSearch(tab) });
      }}
    />
  );
}
